/**
 * Centralized plan configuration — single source of truth.
 *
 * Every UI surface (landing page, pack-selection, dashboard usage card)
 * should import from here instead of keeping its own copy.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PlanFeature {
  text: string;
}

export interface PlanConfig {
  /** Machine-readable code used in DB, APIs, and URL params. */
  packCode: string;
  /** Human-readable name. */
  planName: string;
  /** Base price in USD (0 for free trial). */
  baseAmount: number;
  baseCurrency: 'USD';
  /** Optional: Specific EUR price (for rounded display). */
  eurAmount?: number;
  /** Short label shown next to the price. */
  priceSubtext: string;
  /** One-liner shown below the price. */
  description: string;
  /** CTA label on the landing page. */
  buttonText: string;
  buttonVariant: 'primary' | 'secondary';
  /** Whether this card gets the gradient highlight on the landing page. */
  isHighlighted?: boolean;
  /** Bullet-point features list. */
  features: PlanFeature[];
  /** Rough hiring volume the pack is sized for. */
  hiresPerYear: string;
  /** High-level capability tags. */
  capabilities: string[];
}

// ---------------------------------------------------------------------------
// Plan definitions
// ---------------------------------------------------------------------------

export const PLAN_CONFIGS: PlanConfig[] = [
  {
    packCode: 'free_trial',
    planName: 'Free Trial',
    baseAmount: 0,
    baseCurrency: 'USD',
    priceSubtext: 'introductory',
    description: 'No credit card required',
    buttonText: 'Start Free Trial',
    buttonVariant: 'secondary',
    features: [
      { text: '3 AI-powered interviews' },
      { text: '50 automated CV screenings' },
      { text: '14-day access' },
    ],
    hiresPerYear: 'Evaluation only',
    capabilities: ['Candidate outreach', 'Meta Resumes'],
  },
  {
    packCode: 'payg',
    planName: 'Pay as you go',
    baseAmount: 15,
    baseCurrency: 'USD',
    eurAmount: 14,
    priceSubtext: 'per interview',
    description: 'Billed monthly',
    buttonText: 'Start Pay as you go',
    buttonVariant: 'secondary',
    features: [
      { text: 'No commitment' },
      { text: '$15 per interview' },
    ],
    hiresPerYear: 'Flexible',
    capabilities: ['Candidate outreach', 'Meta Resumes'],
  },
  {
    packCode: 'starter',
    planName: 'Starter Pack',
    baseAmount: 285,
    baseCurrency: 'USD',
    eurAmount: 260,
    priceSubtext: '/ year',
    description: '1 active recruitment',
    buttonText: 'Buy Starter Pack',
    buttonVariant: 'secondary',
    features: [
      { text: '10 AI-powered interviews' },
      { text: '100 CV screenings' },
      { text: '3 talent cloud searches' },
      { text: '10 talent unlocks' },
    ],
    hiresPerYear: '~3 hires / year',
    capabilities: ['Candidate outreach', 'Meta Resumes'],
  },
  {
    packCode: 'core',
    planName: 'Core Pack',
    baseAmount: 1350,
    baseCurrency: 'USD',
    eurAmount: 1240,
    priceSubtext: '/ year',
    description: '5 active recruitments',
    buttonText: 'Buy Core Pack',
    buttonVariant: 'primary',
    isHighlighted: true,
    features: [
      { text: '50 AI-powered interviews' },
      { text: '500 CV screenings' },
      { text: '15 talent cloud searches' },
      { text: '50 talent unlocks' },
    ],
    hiresPerYear: '~10 hires / year',
    capabilities: ['Candidate outreach', 'Meta Resumes'],
  },
  {
    packCode: 'pro',
    planName: 'Pro Pack',
    baseAmount: 2550,
    baseCurrency: 'USD',
    eurAmount: 2340,
    priceSubtext: '/ year',
    description: '10 active recruitments',
    buttonText: 'Buy Pro Pack',
    buttonVariant: 'secondary',
    features: [
      { text: '100 AI-powered interviews' },
      { text: '1,000 CV screenings' },
      { text: '30 talent cloud searches' },
      { text: '100 talent unlocks' },
    ],
    hiresPerYear: '~20 hires / year',
    capabilities: ['Candidate outreach', 'Meta Resumes'],
  },
  {
    packCode: 'gitex_trial',
    planName: 'Gitex Special',
    baseAmount: 0,
    baseCurrency: 'USD',
    priceSubtext: 'limited offer',
    description: '1-year platform access with 5 AI interviews',
    buttonText: 'Claim Gitex Offer',
    buttonVariant: 'primary',
    features: [
      { text: '5 AI-powered interviews for 1 month' },
      { text: '1-year platform access' },
    ],
    hiresPerYear: 'Evaluation',
    capabilities: ['Candidate outreach', 'Meta Resumes'],
  },
  {
    packCode: 'enterprise',
    planName: 'Enterprise',
    baseAmount: -1, // contact us
    baseCurrency: 'USD',
    priceSubtext: '',
    description: 'Custom solution for large organizations',
    buttonText: 'Book a Demo',
    buttonVariant: 'secondary',
    features: [
      { text: 'Custom integrations' },
      { text: 'SLA support' },
    ],
    hiresPerYear: '50+ hires / year',
    capabilities: ['Pro Features', 'ATS Integration', 'Dedicated Support'],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** All paid self-serve pack codes (one-time purchase). */
export const PAID_PACK_CODES = ['starter', 'core', 'pro'] as const;

/** Pack codes that require card saving (not a checkout redirect). */
export const CARD_SAVE_PACK_CODES = ['payg'] as const;

/** Get a single plan config by packCode. */
export const getPlanByCode = (code: string): PlanConfig | undefined =>
  PLAN_CONFIGS.find((p) => p.packCode === code);

/** Only the self-serve paid plans. */
export const getPaidPlans = (): PlanConfig[] =>
  PLAN_CONFIGS.filter((p) => (PAID_PACK_CODES as readonly string[]).includes(p.packCode));

/** The free trial plan (if it exists). */
export const getFreePlan = (): PlanConfig | undefined =>
  PLAN_CONFIGS.find((p) => p.packCode === 'free_trial');

/**
 * Friendly display name for any plan code or plan_kind value.
 * Falls back gracefully for codes without a `PLAN_CONFIGS` entry
 * (e.g. `unlimited`, or a bare `plan_kind` like `fixed` / `payg`).
 */
const PLAN_LABEL_FALLBACKS: Record<string, string> = {
  unlimited: 'Unlimited',
  payg: 'Pay as you go',
  fixed: 'Fixed Plan',
  free_trial: 'Free Trial',
};

export const getPlanDisplayName = (code?: string | null): string => {
  if (!code) return 'Active plan';
  const config = getPlanByCode(code);
  if (config) return config.planName;
  if (PLAN_LABEL_FALLBACKS[code]) return PLAN_LABEL_FALLBACKS[code];
  // Last resort: title-case the raw code (e.g. "some_code" -> "Some Code")
  return code
    .split(/[_\s-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};
